import { computed, ref, type Ref } from "vue";
import { storeToRefs } from "pinia";
import { useConfigStore } from "@/store/config";
import { useCellGroupStore } from "@/store";
import type {
  CellGroupsPostPayload,
  CellGroupsPayload,
  ReportReviewPostPayload,
} from "@/types/api";
import { useApi, useConstants, useCookie } from "@/composables/useAppGlobals";
import { isTaskSaveConflictStatus } from "@/utils/taskAccess";

export function useCells(
  persistenceScope: Readonly<Ref<string | null>> | undefined,
  snapshotVersion: Ref<number | null>,
  taskInstanceId: Ref<string | null>,
) {
  const $api = useApi();
  const $const = useConstants();
  const $cookie = useCookie();

  const isBusy = ref(false);
  const isReviewed = ref(false);
  const hasSaveConflict = ref(false);

  const configStore = useConfigStore();
  const cellGroupStore = useCellGroupStore();

  // expose slideInfo as a computed wrapper around the store so consumers
  // always observe the store's current object (avoids stale references)
  const { slideInfo } = storeToRefs(configStore);
  const currentTaskId = () => {
    const value = slideInfo.value[$const.SLIDE_KEYS.SLIDE_ID];
    return typeof value === "string" || typeof value === "number" ? String(value) : "";
  };
  const canPersist = computed(
    () =>
      !hasSaveConflict.value &&
      !configStore.isReadOnly &&
      Boolean(currentTaskId()) &&
      (persistenceScope === undefined || Boolean(persistenceScope.value)) &&
      typeof snapshotVersion.value === "number" &&
      Boolean(taskInstanceId.value),
  );

  function persistenceOrigin() {
    const taskId = currentTaskId();
    const scope = persistenceScope?.value ?? taskId;
    const version = snapshotVersion.value;
    const instanceId = taskInstanceId.value;
    if (!taskId || !scope || version === null || version === undefined || !instanceId) {
      throw new Error("Task annotation data is not loaded.");
    }
    return { taskId, scope, version, instanceId };
  }

  async function postCells(cellGroupPostPayload: ReportReviewPostPayload): Promise<boolean> {
    if (isBusy.value || !canPersist.value) return false;
    const origin = persistenceOrigin();
    const reviewer = $cookie.getCookie($const.COOKIES.KEY.USER_NAME);
    isBusy.value = true;
    try {
      const resp = await $api.postCells(origin.taskId, {
        ...cellGroupPostPayload,
        task_instance_id: origin.instanceId,
        base_snapshot_version: origin.version,
      } as CellGroupsPostPayload);
      if (
        currentTaskId() !== origin.taskId ||
        (persistenceScope?.value ?? origin.taskId) !== origin.scope
      )
        return false;
      const nextVersion = resp.data.snapshot_version;
      if (typeof nextVersion === "number") {
        snapshotVersion.value = nextVersion;
      }
      // write directly to the store's slideInfo to ensure reactivity for all consumers
      configStore.updateSlideInfoKey(
        $const.SLIDE_KEYS.REVIEW_TIME,
        resp.data[$const.SLIDE_KEYS.REVIEW_TIME],
      );
      configStore.updateSlideInfoKey($const.SLIDE_KEYS.REVIEWER, reviewer);
      isReviewed.value = true;
      return true;
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (!isTaskSaveConflictStatus(status)) throw error;
      if (
        currentTaskId() === origin.taskId &&
        (persistenceScope?.value ?? origin.taskId) === origin.scope
      ) {
        snapshotVersion.value = null;
        taskInstanceId.value = null;
        hasSaveConflict.value = true;
      }
      return false;
    } finally {
      isBusy.value = false;
    }
  }

  const cellBackendRequestObj = computed<CellGroupsPayload>(
    () => cellGroupStore.cellBackendRequestObj,
  );

  async function save(): Promise<boolean> {
    if (!canPersist.value) return false;
    const base = cellBackendRequestObj.value;
    const cellGroupPostPayload: ReportReviewPostPayload = {
      ...base,
      state: (slideInfo.value[$const.SLIDE_KEYS.STATE] as string) || "",
    };
    return postCells(cellGroupPostPayload);
  }

  function clearSaveConflict() {
    hasSaveConflict.value = false;
  }

  function resetState() {
    configStore.resetState();
  }

  return {
    isBusy,
    isReviewed,
    hasSaveConflict,
    canPersist,
    slideInfo,
    cellBackendRequestObj,
    postCells,
    save,
    clearSaveConflict,
    resetState,
  };
}

export default useCells;
